import { useState } from "react";
import ReactModal from "react-modal";
import styled from "styled-components";
import screen from "superior-mq";
import create from "zustand";
import { useBodyScrollLock } from "~/shared/hooks/useBodyScrollLock";
import { bp } from "~/shared/styles/helpers";

/**
 * Custom React hook to manage a modal's open state and associated data.
 *
 * @example
 *   const { modalOpen, modalData, toggleModal } = useModal();
 *   toggleModal("confirmDelete", { id: 42 }); // open
 *   toggleModal(); // close
 *
 * @property {boolean | string} modalOpen - False when closed, or a truthy name/identifier when open.
 * @property {Object<string, any>} modalData - Data associated with the currently open modal.
 * @property {(name?: string | boolean, data?: Object<string, any>) => void} toggleModal - Toggle the modal state.
 *
 *   - If called with no argument or a falsy `name` (default false), the modal is closed and state is reset.
 *   - If called with a truthy `name`, the modal is opened with that name and optional `data`. If the modal is already open,
 *       calling with a name will close it.
 *
 * @returns {{
 *   modalOpen: boolean | string;
 *   modalData: Object<string, any>;
 *   toggleModal: (name?: string | boolean, data?: Object<string, any>) => void;
 * }}
 *   Control handlers and state for a modal.
 */
export const useModal = () => {
  const initialState = {
    isOpen: false,
    data: {},
  };
  const [modal, setModal] = useState(initialState);

  /**
   * @param {string | boolean} name
   * @param {object} data
   */
  const toggleModal = (name = false, data = {}) => {
    if (!name) {
      setModal(initialState);
    } else {
      setModal((prevState) => (!!prevState.isOpen ? initialState : { isOpen: name, data: data }));
    }
  };

  return {
    modalOpen: modal.isOpen,
    modalData: modal.data,
    toggleModal,
  };
};

export const useGlobalModal = create((set) => ({
  modalOpen: false,
  toggleModal: (name = false) => {
    if (!name) return set({ modalOpen: false });

    return set((state) => ({
      modalOpen: state.modalOpen === name ? false : name,
    }));
  },
}));

/** Modal component */
const GullsModal = ({ isOpen, onRequestClose = () => {}, className, appElement = "#root", large, ...props }) => {
  ReactModal.setAppElement(appElement);
  const modalRef = useBodyScrollLock(!!isOpen);

  return (
    <>
      <ReactModal
        isOpen={!!isOpen}
        onRequestClose={onRequestClose}
        overlayClassName="Overlay"
        className="Modal"
        portalClassName={className}
        ref={modalRef}
        {...props}
      />
    </>
  );
};

const Modal = styled(GullsModal)`
  .Overlay {
    z-index: 100;
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: var(--color-ds-scrim);
    padding: 48px 0;

    ${screen.below(
      bp.desktopSm,
      `
      padding: 0 60px;
    `,
    )}

    ${screen.below(
      bp.mobileSm,
      `
      padding: 16px;
    `,
    )}
  }

  .Modal {
    position: relative;
    width: 100%;
    max-width: ${(props) => (props.large ? 896 : 540)}px;
    border: 0;
    outline: none;
    color: var(--color-ds-text-primary);
    background-color: var(--color-ds-surface-overlay);
    padding: 40px 40px 40px 28px;
    border-left: 12px solid ${(props) => (props.error ? "var(--error-red-dark)" : "var(--ultrabright-green)")};
    border-radius: 6px;

    ${screen.below(
      bp.mobileSm,
      `
      padding: 16px;
      border: 0;
    `,
    )}
  }
`;

export default Modal;
